import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import maherFayz from "../assets/images/maherFayz.png";
import hermesSamir from "../assets/images/hermasSamir.png";
import butterlife from "../assets/images/butterlife.png";
import houseOfPrayer from "../assets/images/houseOfPrayer.png";
import qasrAldobara from "../assets/images/qasrAldobara.png";
import SamuelFarouk from "../assets/images/SamuelFarouk.png";
import newLive from "../assets/images/newLive.png";

const MUSICIANS = [
  { 
    src: SamuelFarouk, 
    link: "https://www.youtube.com/playlist?list=PLa1UbfgSw_V3czxjxAc_6MWEmotXhGZHa",
    name: "Samuel Farouk"
  },
  { 
    src: maherFayz, 
    link: "https://www.youtube.com/@ElKarouzTeam",
    name: "Maher Fayz"
  },
  { 
    src: hermesSamir, 
    link: "https://www.youtube.com/@HermasSamir",
    name: "Hermes Samir"
  },
  { 
    src: butterlife, 
    link: "https://www.youtube.com/channel/UC3L8s0htMWtlo4wTJs44lxA",
    name: "Butter Life"
  },
  { 
    src: houseOfPrayer, 
    link: "https://www.youtube.com/@hopkdec",
    name: "House of Prayer"
  },
  { 
    src: qasrAldobara, 
    link: "https://www.youtube.com/@KasreldobaraevangelicalChurch",
    name: "Qasr Aldobara"
  },
  { 
    src: newLive, 
    link: "https://www.youtube.com/playlist?list=PLyEDvV-gx0_yz3KTzM_UGZYACVXgt17t3",
    name: "New Live"
  }
];

const Intro = () => {
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);
  const [showStartArrow, setShowStartArrow] = useState(false);
  const [showEndArrow, setShowEndArrow] = useState(true);
  const isRTL = i18n.dir() === "rtl";

  // Check if scroll arrows should be visible
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScrollPosition = () => {
      if (isRTL) {
        // RTL logic for scroll position
        // In RTL, scrollLeft is often negative and 0 when scrolled all the way left (end)
        // and a large negative value when scrolled all the way right (start).
        const isAtStartRTL = Math.abs(container.scrollLeft) + container.clientWidth >= container.scrollWidth - 10; // Scrolled all the way right (start in RTL)
        const isAtEndRTL = container.scrollLeft > -10; // Scrolled all the way left (end in RTL)

        setShowStartArrow(!isAtEndRTL); // Show start arrow (scroll right) if not at the end (left)
        setShowEndArrow(!isAtStartRTL); // Show end arrow (scroll left) if not at the start (right)
      } else {
        // LTR logic for scroll position
        const isAtStartLTR = container.scrollLeft < 10; // Scrolled all the way left (start in LTR)
        const isAtEndLTR = Math.abs(
          container.scrollWidth - container.clientWidth - container.scrollLeft
        ) < 10; // Scrolled all the way right (end in LTR)

        setShowStartArrow(!isAtStartLTR); // Show start arrow (scroll left) if not at the start (left)
        setShowEndArrow(!isAtEndLTR); // Show end arrow (scroll right) if not at the end (right)
      }
    };

    // Initial check
    checkScrollPosition();
    
    // Setup event listener
    container.addEventListener("scroll", checkScrollPosition);
    window.addEventListener("resize", checkScrollPosition);
    
    // Clean up
    return () => {
      container.removeEventListener("scroll", checkScrollPosition);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [isRTL]);

  const scroll = (direction) => {
    const container = containerRef.current;
    if (!container) return;
    
    const scrollAmount = container.clientWidth * 0.75;
    
    // For RTL mode, we need to invert the scroll direction
    const scrollOffset = isRTL
      ? (direction === "start" ? scrollAmount : -scrollAmount)
      : (direction === "start" ? -scrollAmount : scrollAmount);
    
    container.scrollBy({
      left: scrollOffset,
      behavior: "smooth"
    });
  };

  return (
    <section 
      className="w-full px-4 overflow-hidden"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <h2 className="text-xl md:text-2xl lg:text-[25px] font-normal mb-6 relative inline-block">
        {t("intro.heading")}
        <span className="absolute -bottom-2 left-0 w-20 h-1 bg-gray-800 rounded-full"></span>
      </h2>

      <div className="relative mt-4">
        <div 
          ref={containerRef}
          className="flex overflow-x-auto gap-6 pb-4 scroll-smooth hide-scrollbar snap-x"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none"
          }}
        >
          {MUSICIANS.map((musician, index) => (
            <MusicianCard 
              key={index}
              musician={musician}
              viewText={t("intro.viewChannel")}
            />
          ))}
        </div>
        
        {showStartArrow && (
          <NavigationButton 
            direction="start" 
            onClick={() => scroll("start")}
            isRTL={isRTL}
          />
        )}
        
        {showEndArrow && (
          <NavigationButton 
            direction="end" 
            onClick={() => scroll("end")}
            isRTL={isRTL}
          />
        )}
      </div>

    </section>
  );
};

const MusicianCard = ({ musician, viewText }) => (
  <a 
    href={musician.link}
    target="_blank"
    rel="noopener noreferrer"
    className="flex-none w-[280px] md:w-[200px] group rounded-lg overflow-hidden shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl snap-start"
    aria-label={`View ${musician.name}'s channel`}
  >
    <div className="relative aspect-[197/174] overflow-hidden">
      <img
        src={musician.src}
        alt={musician.name}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-4">
        <div className="text-white text-sm font-medium rounded-full bg-black/50 backdrop-blur-sm px-4 py-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 flex items-center gap-2">
          <PlayIcon />
          {viewText}
        </div>
      </div>
    </div>
  </a>
);

const NavigationButton = ({ direction, onClick, isRTL }) => {
  // Determine which side to place the button based on direction and RTL status
  const position = isRTL
    ? (direction === "start" ? "right" : "left")
    : (direction === "start" ? "left" : "right");
  
  // Adjust the arrow direction based on RTL and button position
  const shouldRotateArrow = (!isRTL && direction === "start") || (isRTL && direction === "end");

  return (
    <button
      onClick={onClick}
      className="absolute top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/80 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-200 max-sm:hidden z-10 shadow-lg hover:scale-110"
      style={{
        [position]: "8px"
      }}
      aria-label={`Scroll ${direction}`}
    >
      <svg 
        className={`w-5 h-5 text-white ${shouldRotateArrow ? "" : "rotate-180"}`} 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
    </button>
  );
};

const PlayIcon = () => (
  <svg 
    className="w-4 h-4" 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);

export default Intro;
